import { provideHttpClient, withFetch, withXsrfConfiguration } from '@angular/common/http';
import {
  APP_INITIALIZER,
  ApplicationConfig,
  ErrorHandler,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection
} from '@angular/core';
import { provideNativeDateAdapter } from '@angular/material/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, withComponentInputBinding, withInMemoryScrolling, withViewTransitions } from '@angular/router';

import { routes } from './app.routes';
import { AuctionHttpRepository } from './core/data/auction/auction.http.repository';
import { AuctionMockRepository } from './core/data/auction/auction.mock.repository';
import { AUCTION_REPOSITORY } from './core/data/auction/auction.repository';
import { CartHttpRepository } from './core/data/cart/cart.http.repository';
import { CartMockRepository } from './core/data/cart/cart.mock.repository';
import { CART_REPOSITORY } from './core/data/cart/cart.repository';
import { CategoryHttpRepository } from './core/data/category/category.http.repository';
import { CategoryMockRepository } from './core/data/category/category.mock.repository';
import { CATEGORY_REPOSITORY } from './core/data/category/category.repository';
import { ListingHttpRepository } from './core/data/listing/listing.http.repository';
import { ListingMockRepository } from './core/data/listing/listing.mock.repository';
import { LISTING_REPOSITORY } from './core/data/listing/listing.repository';
import { OrderHttpRepository } from './core/data/order/order.http.repository';
import { OrderMockRepository } from './core/data/order/order.mock.repository';
import { ORDER_REPOSITORY } from './core/data/order/order.repository';
import { PlatformConfigHttpRepository } from './core/data/platform-config/platform-config.http.repository';
import { PlatformConfigMockRepository } from './core/data/platform-config/platform-config.mock.repository';
import { PLATFORM_CONFIG_REPOSITORY } from './core/data/platform-config/platform-config.repository';
import { provideRepository } from './core/data/provide-repository';
import { SavedSearchHttpRepository } from './core/data/saved-search/saved-search.http.repository';
import { SavedSearchMockRepository } from './core/data/saved-search/saved-search.mock.repository';
import { SAVED_SEARCH_REPOSITORY } from './core/data/saved-search/saved-search.repository';
import { ShopperHttpRepository } from './core/data/shopper/shopper.http.repository';
import { ShopperMockRepository } from './core/data/shopper/shopper.mock.repository';
import { SHOPPER_REPOSITORY } from './core/data/shopper/shopper.repository';
import { StoreHttpRepository } from './core/data/store/store.http.repository';
import { StoreMockRepository } from './core/data/store/store.mock.repository';
import { STORE_REPOSITORY } from './core/data/store/store.repository';
import { TenantHttpRepository } from './core/data/tenant/tenant.http.repository';
import { TenantMockRepository } from './core/data/tenant/tenant.mock.repository';
import { TENANT_REPOSITORY } from './core/data/tenant/tenant.repository';
import { WatchlistHttpRepository } from './core/data/watchlist/watchlist.http.repository';
import { WatchlistMockRepository } from './core/data/watchlist/watchlist.mock.repository';
import { WATCHLIST_REPOSITORY } from './core/data/watchlist/watchlist.repository';
import { GlobalErrorHandler } from './core/services/error-handler.service';
import { PerformanceService } from './core/services/performance.service';
import { ShopperSessionService } from './core/services/shopper-session.service';
import { TenantHostService } from './core/services/tenant-host.service';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withViewTransitions(),
      withInMemoryScrolling({ anchorScrolling: 'enabled', scrollPositionRestoration: 'enabled' })
    ),
    provideAnimationsAsync(),
    provideNativeDateAdapter(),
    provideHttpClient(withFetch(), withXsrfConfiguration({ cookieName: 'XSRF-TOKEN', headerName: 'X-XSRF-TOKEN' })),
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    // Domain repositories - the ONLY mock↔HTTP swap point (environment.useMockData).
    // Provided at root (not per-route) because the always-on shell reads cart,
    // watchlist, tenant and category data on every page.
    provideRepository(LISTING_REPOSITORY, ListingMockRepository, ListingHttpRepository),
    provideRepository(AUCTION_REPOSITORY, AuctionMockRepository, AuctionHttpRepository),
    provideRepository(CART_REPOSITORY, CartMockRepository, CartHttpRepository),
    provideRepository(ORDER_REPOSITORY, OrderMockRepository, OrderHttpRepository),
    provideRepository(SHOPPER_REPOSITORY, ShopperMockRepository, ShopperHttpRepository),
    provideRepository(WATCHLIST_REPOSITORY, WatchlistMockRepository, WatchlistHttpRepository),
    provideRepository(SAVED_SEARCH_REPOSITORY, SavedSearchMockRepository, SavedSearchHttpRepository),
    provideRepository(TENANT_REPOSITORY, TenantMockRepository, TenantHttpRepository),
    provideRepository(STORE_REPOSITORY, StoreMockRepository, StoreHttpRepository),
    provideRepository(CATEGORY_REPOSITORY, CategoryMockRepository, CategoryHttpRepository),
    provideRepository(PLATFORM_CONFIG_REPOSITORY, PlatformConfigMockRepository, PlatformConfigHttpRepository),
    // Host-header tenant resolution (SM-02). Blocks the first render, so a
    // branded subdomain paints its own colors rather than flashing marketplace
    // chrome first. Declared after the repositories it reads.
    provideAppInitializer(() => inject(TenantHostService).resolveHostTenant()),
    // Shared shopper identity (SM-04). Turns the apex-domain id cookie back
    // into a profile before the first render, so the shell never paints as a
    // guest and then correct itself.
    provideAppInitializer(() => inject(ShopperSessionService).restoreSession()),
    {
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: () => {
        const performanceService = inject(PerformanceService);
        return () => performanceService.init();
      }
    }
  ]
};
